// Embed Configuration for Chatbot Widget
// This file contains all configuration options for the embeddable chatbot

import { env } from './EnvironmentConfig';
import { DESIGN_TOKENS } from './DesignSystem';

export const EMBED_CONFIG = {
  // API Configuration
  API: {
    BASE_URL: env.BASE_URL,
    WEBSOCKET_URL: env.API_WEBSOCKET,
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000
  },

  // Widget Appearance
  WIDGET: {
    // Default dimensions
    DEFAULT_WIDTH: '400px',
    DEFAULT_HEIGHT: '600px',
    MIN_WIDTH: '320px',
    MIN_HEIGHT: '400px',
    MAX_WIDTH: '800px',
    MAX_HEIGHT: '800px',

    // Position options
    POSITIONS: {
      BOTTOM_RIGHT: 'bottom-right',
      BOTTOM_LEFT: 'bottom-left',
      TOP_RIGHT: 'top-right',
      TOP_LEFT: 'top-left',
      CENTER: 'center',
      INLINE: 'inline'
    },

    // Default position
    DEFAULT_POSITION: 'bottom-right',

    // Margins from screen edges
    MARGIN: '20px',

    // Z-index for floating widgets
    Z_INDEX: 10000,

    // Animation settings
    ANIMATION: {
      DURATION: '300ms',
      EASING: 'cubic-bezier(0.4, 0, 0.2, 1)'
    }
  },

  // Theme Configuration
  THEME: {
    // Default theme
    DEFAULT: {
      PRIMARY_COLOR: DESIGN_TOKENS.COLORS.PRIMARY[500],
      SECONDARY_COLOR: DESIGN_TOKENS.COLORS.GRAY[600],
      BACKGROUND_COLOR: DESIGN_TOKENS.COLORS.BACKGROUND.CARD,
      TEXT_COLOR: DESIGN_TOKENS.COLORS.GRAY[800],
      BORDER_COLOR: DESIGN_TOKENS.COLORS.GRAY[200],
      BORDER_RADIUS: DESIGN_TOKENS.BORDERS.RADIUS.LG,
      SHADOW: DESIGN_TOKENS.SHADOWS.LG,
      FONT_FAMILY: DESIGN_TOKENS.TYPOGRAPHY.FONT_FAMILY.PRIMARY
    },

    // Minimal theme
    MINIMAL: {
      PRIMARY_COLOR: '#000000',
      SECONDARY_COLOR: '#666666',
      BACKGROUND_COLOR: '#ffffff',
      TEXT_COLOR: '#333333',
      BORDER_COLOR: '#e0e0e0',
      BORDER_RADIUS: '8px',
      SHADOW: '0 2px 8px rgba(0, 0, 0, 0.1)',
      FONT_FAMILY: 'system-ui, -apple-system, sans-serif'
    }
  },

  // Authentication Configuration
  AUTH: {
    // Authentication methods - JWT only for now
    METHODS: {
      TOKEN: 'token'           // Use provided JWT token only
    },

    // Default method - JWT only
    DEFAULT_METHOD: 'token',

    // Token storage
    STORAGE_KEY: 'chatbot_embed_auth',
    
    // Chat session management
    CHAT_ID_KEY: 'chatbot_chat_id',
    DEFAULT_CHAT_ID: '1',
    
    // Agent configuration
    AGENT_ID_KEY: 'chatbot_agent_id'
  },

  // Chat Configuration
  CHAT: {
    // Message limits
    MAX_MESSAGE_LENGTH: 2000,
    MAX_MESSAGES_DISPLAY: 100,

    // Typing indicator
    TYPING_INDICATOR_DELAY: 500,

    // Auto-scroll behavior
    AUTO_SCROLL: true,

    // Message timestamps
    SHOW_TIMESTAMPS: false,

    // Welcome message
    WELCOME_MESSAGE: "Hello! How can I help you today?",

    // Placeholder text
    INPUT_PLACEHOLDER: "Type your message...",

    // Error messages
    ERROR_MESSAGES: {
      NETWORK: "Connection error. Please try again.",
      RATE_LIMIT: "Too many messages. Please wait a moment.",
      AGENT_UNAVAILABLE: "Agent is currently unavailable.",
      CHAT_UNAVAILABLE: "Chat is currently unavailable. Please try again later or contact support.",
      GENERIC: "Something went wrong. Please try again.",

      // Subscription limit messages
      FREE_LIMIT_REACHED: "You've reached your free plan limit. Upgrade to continue chatting.",
      SUBSCRIPTION_LIMIT_REACHED: "You've reached your plan's usage limit. Please upgrade or wait for the next billing cycle.",
      MESSAGE_LIMIT_REACHED: "Daily message limit reached. Try again tomorrow or upgrade your plan.",

      // Authentication errors
      AUTH_REQUIRED: "Authentication required to continue chatting.",
      TOKEN_EXPIRED: "Your session has expired. Please refresh the page.",
      INVALID_TOKEN: "Invalid authentication token. Please refresh the page."
    },

    // Limit notification settings
    LIMIT_NOTIFICATIONS: {
      SHOW_WARNING_AT_PERCENTAGE: 80, // Show warning when 80% of limit is reached
      WARNING_MESSAGE: "You're approaching your usage limit.",
      UPGRADE_CTA: "Upgrade your plan for unlimited access."
    }
  },

  // Security Configuration
  SECURITY: {
    // Allowed origins for iframe embedding
    ALLOWED_ORIGINS: ['*'], // Configure this for production
    
    // Content Security Policy
    CSP_DIRECTIVES: {
      'default-src': "'self'",
      'connect-src': "'self' wss: https:",
      'img-src': "'self' data: https:",
      'style-src': "'self' 'unsafe-inline'",
      'font-src': "'self' data:"
    },
    
    // Rate limiting
    RATE_LIMIT: {
      MESSAGES_PER_MINUTE: 30,
      MESSAGES_PER_HOUR: 200
    }
  },

  // Feature Flags
  FEATURES: {
    VOICE_INPUT: false,        // Enable voice input
    FILE_UPLOAD: true,         // Enable file uploads
    EMOJI_PICKER: false,       // Enable emoji picker
    MESSAGE_REACTIONS: false,  // Enable message reactions
    TYPING_INDICATOR: true,    // Show typing indicator
    READ_RECEIPTS: false,      // Show read receipts
    MESSAGE_HISTORY: true,     // Persist message history
    MINIMIZE_WIDGET: true,     // Allow widget minimization
    FULLSCREEN_MODE: false     // Allow fullscreen mode
  },

  // Analytics Configuration
  ANALYTICS: {
    ENABLED: false,
    TRACK_EVENTS: [
      'widget_opened',
      'widget_closed',
      'message_sent',
      'message_received',
      'error_occurred'
    ]
  },

  // Branding Configuration
  BRANDING: {
    SHOW_POWERED_BY: true,
    POWERED_BY_TEXT: "Powered by VLM",
    POWERED_BY_URL: "https://voiceracx.ai/",
    LOGO_URL: null,
    CUSTOM_CSS: null
  }
};

// Utility functions for embed configuration
export const getEmbedTheme = (themeName = 'default') => {
  // Only support DEFAULT and MINIMAL themes, fallback to DEFAULT
  const upperThemeName = themeName.toUpperCase();
  if (upperThemeName === 'MINIMAL') {
    return EMBED_CONFIG.THEME.MINIMAL;
  }
  return EMBED_CONFIG.THEME.DEFAULT;
};

export const validateEmbedConfig = (config) => {
  const errors = [];

  // Agent ID is required for opening the embed page
  if (!config.agentId) {
    errors.push('Agent ID is required');
  }

  // Base URL is optional - will fall back to environment config if not provided
  // Only validate format if provided
  if (config.baseUrl && !isValidUrl(config.baseUrl)) {
    errors.push('Invalid base URL format');
  }

  if (config.width && !isValidDimension(config.width)) {
    errors.push('Invalid width dimension');
  }

  if (config.height && !isValidDimension(config.height)) {
    errors.push('Invalid height dimension');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

const isValidDimension = (dimension) => {
  return /^\d+(px|%|em|rem|vh|vw)$/.test(dimension);
};

const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const mergeEmbedConfig = (userConfig = {}) => {
  return {
    ...EMBED_CONFIG,
    ...userConfig,
    WIDGET: {
      ...EMBED_CONFIG.WIDGET,
      ...userConfig.WIDGET
    },
    THEME: {
      ...EMBED_CONFIG.THEME,
      ...userConfig.THEME
    },
    CHAT: {
      ...EMBED_CONFIG.CHAT,
      ...userConfig.CHAT
    },
    FEATURES: {
      ...EMBED_CONFIG.FEATURES,
      ...userConfig.FEATURES
    }
  };
};

export default EMBED_CONFIG;
